/**
 * Resume-or-start-over modal, shown at boot when an incomplete session
 * exists (main.js) and at Home when the user taps a different list
 * while a session on another list is active (home.js branch (c)).
 *
 * Intentionally NOT ESC-dismissable: the user MUST pick Resume or
 * Start Over. The design flow requires a definite choice to reach Home.
 *
 * Signature matches the Batch 2 stub in src/ui/modal-stubs.js:
 *   showResumeModal({ listName, currentIndex, total, onResume, onStartOver })
 */

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * @param {{
 *   listName: string,
 *   currentIndex: number,
 *   total: number,
 *   onResume: () => void,
 *   onStartOver: () => void
 * }} opts
 */
export function showResumeModal(opts) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay modal-overlay--blocking';
  overlay.innerHTML = `
    <div class="modal-dialog modal-dialog--resume" role="dialog" aria-modal="true"
         aria-labelledby="resume-heading">
      <h2 id="resume-heading" class="modal-dialog__heading">Resume your run?</h2>
      <p class="modal-dialog__message">
        ${escapeHtml(opts.listName)} — ${opts.currentIndex} of ${opts.total}
      </p>
      <div class="modal-dialog__actions modal-dialog__actions--stacked">
        <button type="button" class="modal-btn modal-btn--confirm" data-action="resume">
          Resume
        </button>
        <button type="button" class="modal-btn modal-btn--cancel" data-action="start-over">
          Start Over
        </button>
      </div>
    </div>
  `;

  const resumeBtn = overlay.querySelector('[data-action="resume"]');
  const startOverBtn = overlay.querySelector('[data-action="start-over"]');

  function close() {
    document.removeEventListener('keydown', onKeyDown, true);
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  }

  function onKeyDown(e) {
    // Block ESC (see block comment above).
    if (e.key === 'Escape') {
      e.preventDefault();
      return;
    }
    // Focus trap between two buttons.
    if (e.key === 'Tab') {
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === startOverBtn) {
          e.preventDefault();
          resumeBtn.focus();
        } else if (active === resumeBtn) {
          e.preventDefault();
          startOverBtn.focus();
        }
      } else {
        if (active === resumeBtn) {
          e.preventDefault();
          startOverBtn.focus();
        } else if (active === startOverBtn) {
          e.preventDefault();
          resumeBtn.focus();
        }
      }
    }
  }

  resumeBtn.addEventListener('click', () => {
    close();
    try { opts.onResume(); } catch (_e) { /* caller bug */ }
  });
  startOverBtn.addEventListener('click', () => {
    close();
    try { opts.onStartOver(); } catch (_e) { /* caller bug */ }
  });
  // Overlay click does nothing on the resume modal (blocking).
  document.addEventListener('keydown', onKeyDown, true);

  document.body.appendChild(overlay);
  resumeBtn.focus();
}
